import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Trash2, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const MAX_SECONDS = 300;
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export const VoicePlayer = ({ taskId, note, testId }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let u;
    api.get(`/tasks/${taskId}/voice/${note.id}`, { responseType: "blob" })
      .then((r) => { u = URL.createObjectURL(r.data); setUrl(u); })
      .catch(() => setFailed(true));
    return () => u && URL.revokeObjectURL(u);
  }, [taskId, note.id]);
  return (
    <div data-testid={testId || `voice-note-${note.id}`} className="flex items-center gap-2 rounded-lg border border-[#282E3D] bg-[#0B0D12] px-2 py-1.5">
      <Mic className="h-3.5 w-3.5 shrink-0 text-[#FF6B00]" />
      {failed ? <span className="text-xs text-rose-300">Couldn't load voice note</span>
        : url ? <audio controls preload="metadata" src={url} className="h-8 w-full min-w-0" data-testid={`voice-audio-${note.id}`} />
        : <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Loader2 className="h-3 w-3 animate-spin" />Loading…</span>}
      {note.duration != null && <span className="shrink-0 font-mono text-[10px] text-slate-500">{fmt(note.duration)}</span>}
    </div>
  );
};

export const VoiceRecorder = ({ notes, setNotes, prefix = "voice" }) => {
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const rec = useRef(null);
  const timer = useRef(null);
  const started = useRef(0);

  useEffect(() => () => { clearInterval(timer.current); rec.current?.stream.getTracks().forEach((t) => t.stop()); }, []);

  const stop = () => {
    clearInterval(timer.current);
    if (rec.current?.state === "recording") rec.current.stop();
    setRecording(false);
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") return toast.error("Voice recording isn't supported in this browser");
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      return toast.error("Microphone access was blocked. Allow it in your browser to record.");
    }
    const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => MediaRecorder.isTypeSupported?.(m));
    const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks, { type: (r.mimeType || "audio/webm").split(";")[0] });
      const duration = (Date.now() - started.current) / 1000;
      if (blob.size) setNotes((n) => [...n, { blob, duration, url: URL.createObjectURL(blob) }]);
    };
    rec.current = r;
    started.current = Date.now();
    r.start();
    setSecs(0);
    setRecording(true);
    timer.current = setInterval(() => {
      const s = (Date.now() - started.current) / 1000;
      setSecs(s);
      if (s >= MAX_SECONDS) stop();
    }, 250);
  };

  return (
    <div className="space-y-2">
      {notes.map((n, i) => (
        <div key={n.url} data-testid={`${prefix}-pending-${i}`} className="flex items-center gap-2 rounded-lg border border-[#FF6B00]/30 bg-[#FF6B00]/5 px-2 py-1.5">
          <audio controls src={n.url} className="h-8 w-full min-w-0" />
          <span className="shrink-0 font-mono text-[10px] text-slate-400">{fmt(n.duration)}</span>
          <button type="button" data-testid={`${prefix}-remove-${i}`} onClick={() => { URL.revokeObjectURL(n.url); setNotes(notes.filter((_, j) => j !== i)); }} className="shrink-0 text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      {recording ? (
        <button type="button" data-testid={`${prefix}-stop-button`} onClick={stop} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-rose-500/15 px-4 py-2.5 text-sm font-semibold text-rose-300 hover:bg-rose-500/25">
          <span className="h-2 w-2 animate-pulse rounded-full bg-rose-500" /><Square className="h-3.5 w-3.5" />Stop recording · {fmt(secs)}
        </button>
      ) : (
        <button type="button" data-testid={`${prefix}-record-button`} onClick={start} className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[#282E3D] px-4 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:border-[#FF6B00]/60 hover:text-white">
          <Mic className="h-4 w-4 text-[#FF6B00]" />{notes.length ? "Record another voice note" : "Record a voice note"}
        </button>
      )}
    </div>
  );
};

export const feedbackForm = (comment, notes) => {
  const fd = new FormData();
  fd.append("comment", comment);
  notes.forEach((n, i) => fd.append("voice", n.blob, `voice-${i}.${n.blob.type.includes("mp4") ? "m4a" : n.blob.type.includes("ogg") ? "ogg" : "webm"}`));
  fd.append("durations", notes.map((n) => n.duration.toFixed(1)).join(","));
  return fd;
};
